import { ChangeDetectionStrategy, Component, ViewChild, Injector } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { SubSink } from 'subsink2';
import { MatTabGroup } from '@angular/material/tabs';
import { AppLoginContainerModelUpdate, app_login_subject_RowClass, Pagination, PagingResponseModel, TenantUserProfileService, TenantUserRoleContainerModel, TenantUserRoleService } from 'blg-akaun-ts-lib';
import { Observable, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { ViewColumnComponent } from '../../../../../../../view-column.component';
import { PaginationComponent } from '../../../../../../../utilities/pagination/pagination.component';
import { AppConfig } from '../../../../../../../visa';
import { gridOptionsServerSide, pageFiltering, pageSorting, serverSideClear } from '../../../../../../../listing.utils';
import { PermissionFacade } from '../../../../../../../modules/permission/facades/permission.facade';
import { RolePermStates } from '../../../../../../../modules/permission/role-permissions-controller/states';
import { RolePermSelectors } from '../../../../../../../modules/permission/role-permissions-controller/selectors';
import { RolePermActions } from '../../../../../../../modules/permission/role-permissions-controller/actions';
import { PermissionStates } from '../../../../../permission-controller/root';
import { TranslateService } from "@ngx-translate/core";

interface LocalState {
  deactivateReturn: boolean;
  selectedIndex: number;
}

interface UserModel {
  app_login_subject_guid: string;
  app_login_subject_mobile: string,
  app_login_subject_email: string,
  app_login_subject_name: string,
}

@Component({
  selector: 'app-role-perm-user-add',
  templateUrl: './role-perm-user-add.component.html',
  styleUrls: ['./role-perm-user-add.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ComponentStore]
})
export class RolePermUserAddComponent extends ViewColumnComponent {

  @ViewChild(PaginationComponent, {static: false})
  private paginationComponent: PaginationComponent;
  @ViewChild(MatTabGroup, {static: true}) matTab: MatTabGroup;

  protected readonly index = 3;
  private localState: LocalState;

  // initialise local states
  readonly localState$ = this.viewColFacade.selectLocalState(this.index);
  readonly deactivateReturn$ = this.componentStore.select(state => state.localState.deactivateReturn);
  readonly selectedIndex$ = this.componentStore.select(state => (state.localState.selectedIndex));

  // previous index
  prevIndex: number;
  private prevLocalState: any;

  // api visa
  apiVisa = AppConfig.apiVisa;

  // subSink is to ensure all subscribe calls are unsubscribed to once the component is destroyed
  private subSink = new SubSink;

  formDisabled: boolean = true;

  // search model
  searchValue: any = '';

  // initialise ag grid
  gridApi;
  gridOptions = gridOptionsServerSide;
  columnsDefs;
  rowSelection = 'multiple';

  // users that are already added
  addedEntities = new Map<string, any>();
  // currently selected users
  selectedEntities = new Map<string, any>();

  roleGuid: string;

  //translation variable
  back_button: string;
  title: string;
  add_button: string;
  search_text: string;
  search_email_or_mobile_text: string;
  email_text: string;
  mobile_text: string;
  name_text: string;

  constructor(
    private tenantUserProfileService: TenantUserProfileService,
    private tenantUserRoleService: TenantUserRoleService,
    private viewColFacade: PermissionFacade,
    private readonly store: Store<PermissionStates>,
    private readonly componentStore: ComponentStore<{localState: LocalState}>,
    private injector: Injector
  ) {
    super();
    const translateService = this.injector.get(TranslateService, null);
    if (translateService) {
      translateService.get('labels').subscribe(res => {
        this.back_button = res.back_button;
        this.title = res.add_user;
        this.add_button = res.add_button_caps;
        this.search_text = res.search;
        this.search_email_or_mobile_text = res.search_email_or_mobile;
        this.email_text = res.email;
        this.mobile_text = res.mobile;
        this.name_text = res.name;
      });
    } else {
      this.back_button = 'Back';
      this.title = 'Add User';
      this.add_button = 'ADD';
      this.search_text = 'Search';
      this.search_email_or_mobile_text = 'Search user email or mobile'
      this.email_text = 'Email';
      this.mobile_text = 'Mobile';
      this.name_text = 'Name';
    }
  }

  /**
   * Lifecycle method: Called when the component is first initialised.
   */
  ngOnInit(): void {
    this.subSink.sink = this.localState$.subscribe(a => {
      this.localState = a;
      this.componentStore.setState({localState: a});
    });
    this.subSink.sink = this.viewColFacade.prevIndex$.subscribe(resolve => this.prevIndex = resolve);
    this.subSink.sink = this.viewColFacade.prevLocalState$().subscribe(resolve => this.prevLocalState = resolve);

    // define column definitions
    this.columnsDefs = [
      {
        headerName: '',
        field: 'app_login_subject_guid',
        valueFormatter: params => '',
        checkboxSelection: true,
        cellStyle: params => {
          return this.addedEntities.has(params.value) ?
          {'pointer-events': 'none', textAlign: 'left'} : {textAlign: 'left'}
        },
        floatingFilter: false,
        sortable: false,
        minWidth: 0,
      },
      {
        headerName: this.email_text,
        field: 'app_login_subject_email',
        cellStyle: {textAlign: 'left'}
      },
      {
        headerName: this.mobile_text,
        field: 'app_login_subject_mobile',
        cellStyle: {textAlign: 'left'}
      },
      {
        headerName: this.name_text,
        field: 'app_login_subject_name',
        cellStyle: {textAlign: 'left'}
      }

    ];

    this.subSink.sink = this.store.select(RolePermSelectors.selectGuid)
    .subscribe(x => {
      this.roleGuid = x;

      // get all current users in role
      this.subSink.sink = this.getUsersInRole().subscribe(x => {
        // add users
        x.forEach(a => {
          this.addedEntities.set(a.link_subject_to_role.app_subject_guid.toString(), a.link_subject_to_role);
        })
      })
    })

  }

  /**
   * Function called when AG grid is initialised.
   *
   * @param params : grid parameters
   */
   onGridReady(params) {
    this.gridApi = params.api;
    this.gridApi.closeToolPanel();
    const datasource = this.getDatasource();
    this.gridApi.setServerSideDatasource(datasource);

    // resize columns
    const allColIds = params.columnApi.getAllColumns().map(x => x.colId);
    params.columnApi.autoSizeColumns(allColIds);

    this.subSink.sink = this.store.select(RolePermSelectors.selectUpdateUserRoleListing)
      .subscribe(a => {

        if (a) {
          // get all current users in role
          this.subSink.sink = this.getUsersInRole().subscribe(x => {
            this.addedEntities.clear();
            // add members
            x.forEach(a => {
              this.addedEntities.set(a.link_subject_to_role.app_subject_guid.toString(), a.link_subject_to_role)
            });
            this.gridApi.refreshServerSideStore(
              { purge: true }
            )
            this.store.dispatch(RolePermActions.setUpdateUserRoleListing({update: false}))
          })
        }
      })

  }

  /**
   * Sets the datasource on the AG grid to response data returned from the
   * getByCriteria API.
   *
   * @param conditionalCriteria : any custom criteria to search for in the API
   * @returns datasource for AG grid
   */
  getDatasource(conditionalCriteria?) {

    let offset = 0;
    let limit = this.paginationComponent.rowPerPage;

    let criteria = [
      {columnName: 'calcTotalRecords', operator: '=', value: 'true'},
    ];

    if (conditionalCriteria) {
      criteria = criteria.concat(conditionalCriteria);
    }

    return {

      getRows: grid => {

        const sortModel = grid.request.sortModel;
        const filterModel = grid.request.filterModel;
        const sortOn = pageSorting(sortModel);
        const filter = pageFiltering(filterModel);

        // only change offset and limit if no filterModel or sortModel is present
        if (Object.keys(filterModel).length == 0 && sortModel.length == 0) {
          offset = grid.request.startRow;
          limit = grid.request.endRow - offset;
        }

        const pagination = new Pagination(offset, limit, criteria);

        this.subSink.sink = this.tenantUserProfileService.getByCriteria(pagination, this.apiVisa)
          .pipe(

            map((x: PagingResponseModel<AppLoginContainerModelUpdate>) => {
              const a = x.data;
              const users: UserModel[] = [];

              a
              .map(x => x.appLoginSubject)
              .forEach( subject  => {

                const hasMobile = subject?.main_phone && subject.main_phone?.toString().trim() !== '';
                const hasEmail = subject?.main_email && subject.main_email?.toString().trim() !== '';
                const hasName = subject?.name && subject.name?.toString().trim() !== '';
                const userModel: UserModel = {
                  app_login_subject_guid: subject.guid.toString(),
                  app_login_subject_mobile: hasMobile ? subject.main_phone?.toString() : '--',
                  app_login_subject_email: hasEmail ? subject.main_email?.toString() : '--' + subject.name?.toString(),
                  app_login_subject_name: hasName ? subject.name?.toString() : '--',
                }

                if (hasMobile || hasEmail) {
                  users.push(userModel);
                }
                else { // handing empty mobile or email. will show "--" for empty mobile or email
                  const userModel: UserModel = {
                    app_login_subject_guid: subject.guid.toString(),
                    app_login_subject_mobile: '--',
                    app_login_subject_email:  '--',
                    app_login_subject_name: hasName ? subject.name?.toString() : '--',
                  }
                  users.push(userModel);
                }

              });

              return {
                ...x,
                data: users
              }
            })
          ).subscribe(a => {
            // put data into the grid
            const data = sortOn(a.data).filter(entity => filter.by(entity));
            const totalRecords = filter.isFiltering ? a.totalRecords : data.length;
            // force pagination to go to first page if filter is applied
            if (this.paginationComponent.currentPage > this.paginationComponent.totalPage.value) { this.paginationComponent.firstPage() }

            grid.successCallback(data, totalRecords);
            this.setSelectedNodes();
          }, err => {
            grid.failCallback();
          })
      }
    }
  }

  /**
   * Returns an observable containing all the users that are assigned to this
   * role.
   *
   * @returns an observable containing all the users that are assigned to this role
   */
  getUsersInRole(): Observable<TenantUserRoleContainerModel[]> {
    const criteria = [
      {columnName: 'calcTotalRecords', operator: '=', value: 'true'},
      {columnName: 'app_mst_role_guid', operator: '=', value: this.roleGuid}
    ];
    const pagination = new Pagination(null, null, criteria);

    return this.tenantUserRoleService.getByCriteria(pagination, this.apiVisa)
      .pipe(
        map(x => x.data)
      )
  }

  /**
   * Reset the search bar and search value.
   */
   reset() {
    this.searchValue = '';
    const datasource = this.getDatasource();
    this.gridApi.setServerSideDatasource(datasource);
  };

  /**
   * Add the users to the role.
   */
  onAdd() {
    const guids = [...this.selectedEntities.keys()];
    // clear selected
    this.selectedEntities.clear();
    // dispatch ngrx action to add users to the role
    this.store.dispatch(RolePermActions.addUsersToRoleInit({subjectGuids: guids, roleGuid: this.roleGuid}));
    // set form to disable again
    this.formDisabled = true;
  };

  /**
   * This function is called every time the user searches something in the
   * search bar.
   *
   */
   /**
   * Runs the search bar's keyword against the listing's own endpoint.
   *
   * Replaces the raw SELECT that went through SubQueryService:
   *
   *   FROM app_login_subject hdr
   *   WHERE (hdr.main_email ILIKE '%x%' OR hdr.main_phone ILIKE '%x%')
   *     AND hdr.status != 'DELETED'
   *
   * GET user-profile/query -> AppLoginUow.getByCriteriaForTenantUserProfile ->
   * AppLoginUowHelper.generateWhereClause applies `search_word` through
   * generateSearchWordCondition as a bound ILIKE over main_email, main_phone,
   * name, name_first, name_last and the joined principal_id, and defaults to
   * `status != 'DELETED'`.
   *
   * Behaviour notes: the keyword also matches the subject's name and principal
   * id. A keyword that is itself a valid UUID is skipped by that helper, so guid
   * look-ups keep going through the dedicated guid criteria.
   */
  onSearch() {
    const datasource = this.getDatasource(this.buildSearchCriteria());
    this.gridApi.setServerSideDatasource(datasource);
  }

  /**
   * Criteria for the current keyword, or undefined when the box is empty so the
   * listing falls back to its default criteria.
   */
  buildSearchCriteria() {
    const keyword = (this.searchValue || '').toString().trim();
    if (!keyword) {
      return undefined;
    }
    return [
      { columnName: 'search_word', operator: '=', value: keyword }
    ];
  }

  /**
   * This function is to be called every time the grid refreshes.
   * Set nodes that are already in the permissions template or previously currently
   * selected to selected.
   *
   */
   setSelectedNodes() {
    this.gridApi.forEachNode(node => {
      if (node.data) {
        const guid = node.data.app_login_subject_guid;
        const inRole = this.addedEntities.has(guid);
        const isCurrentlySelected = this.selectedEntities.has(guid);
        if (inRole || isCurrentlySelected) {
          node.setSelected(true);
        } else {
          node.setSelected(false);
        }
      }
    })
  }

  /**
   * This function is called when a row is either selected or deselected.
   * Updated the currentlySelected array.
   */
  onRowSelected(event) {
    const selectedGuid = (event.node) ? event.node.data.app_login_subject_guid : null;
    const isSelectionEvent = event.node.isSelected();
    const inRole = this.addedEntities.has(selectedGuid);
    const isCurrentlySelected = this.selectedEntities.has(selectedGuid);

    if (!isSelectionEvent && isCurrentlySelected && !inRole) {
      // delete from selected entities if node is not already in the role,
      // it is a deselection event and the node is in the currently selected entities
      this.selectedEntities.delete(selectedGuid)
    } else if (isSelectionEvent && !inRole && !isCurrentlySelected) {
      // add node to the currently selected entities if it is a selection event,
      // the node is not currently already in the team and the node is
      // not in the currently selected maps already
      this.selectedEntities.set(selectedGuid, event.node.data)
    }

    if (this.selectedEntities.size > 0) {
      this.formDisabled = false;
    } else {
      this.formDisabled = true;
    }
  }

  /**
   * Return to the previous drawer/view column.
   */
   onReturn() {
    this.viewColFacade.updateInstance(this.prevIndex, {
      ...this.prevLocalState,
      deactivateReturn: false,
      deactivateAdd: false,
      deactivateList: false
    });
    this.viewColFacade.onPrev(this.prevIndex);
  }

  /**
   * Lifecycle method: Called when this component is destroyed.
   */
  ngOnDestroy() {
    if (this.matTab) {
      this.viewColFacade.updateInstance(this.index, {
        ...this.localState,
        selectedIndex: this.matTab.selectedIndex
      });
    }
    this.subSink.unsubscribe();
  }

}
